import { useEffect, useState, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useAuth } from "@/modules/Auth/context/AuthContext"
import { EventoDataType } from "@/types/events"
import { Skeleton } from "@/components/ui/skeleton"
import { getJornadaEmpresa } from "@/services/companyService"
import { EventCardV2 } from "@/modules/Events/components/EventCardV2"
import { MemberModal } from "@/modules/Users/components/MemberModal"
import { useNavigate, useSearchParams } from "react-router-dom"
import { PageHeader } from "@/components/Molecules/PageHeader/PageHeader"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { MySubscriptionsFilter } from "../components/MySubscriptionsFilter"

type TabType = "ativas" | "passadas"

type SubscribedEvent = EventoDataType & {
  status_origem?: "presença autorizada" | "presença não autorizada" | "pendente de aprovação"
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
}

const EventsMySubscriptionsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [events, setEvents] = useState<SubscribedEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [isMemberModalOpened, setIsMemberModalOpened] = useState(false)
  const [memberId, setMemberId] = useState<string | number>()
  const [searchParams, setSearchParams] = useSearchParams()

  // Filter states
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState("todos")
  const [periodFilter, setPeriodFilter] = useState("todos")
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)

  const activeTab = (searchParams.get("activeTab") || "ativas") as TabType

  const handleTabChange = (value: string) => {
    setSearchParams({ activeTab: value })
  }

  useEffect(() => {
    if (!user?.pesId) {
      setLoading(false)
      return
    }

    const loadEvents = async () => {
      try {
        setLoading(true)
        const response = await getJornadaEmpresa(user.empId, {
          pes_id: String(user.pesId),
        })
        setEvents(response.data.data)
      } catch {
        setEvents([])
      } finally {
        setLoading(false)
      }
    }
    loadEvents()
  }, [user])

  const filteredEvents = useMemo(() => {
    if (!Array.isArray(events)) return []

    return events.filter((ev) => {
      const matchesSearch = !search || ev.descricao.toLowerCase().includes(search.toLowerCase())
      const matchesType =
        typeFilter === "todos" || ev.formato?.toLowerCase() === typeFilter.toLowerCase()

      let matchesPeriod = true
      const eventDate = new Date(ev.data_inicio)
      const today = new Date()

      if (periodFilter === "este-mes") {
        matchesPeriod =
          eventDate.getMonth() === today.getMonth() &&
          eventDate.getFullYear() === today.getFullYear()
      } else if (periodFilter === "proximo-mes") {
        const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1)
        matchesPeriod =
          eventDate.getMonth() === nextMonth.getMonth() &&
          eventDate.getFullYear() === nextMonth.getFullYear()
      } else if (periodFilter === "proximos-3-meses") {
        const threeMonthsOut = new Date(today.getFullYear(), today.getMonth() + 3, today.getDate())
        matchesPeriod = eventDate >= today && eventDate <= threeMonthsOut
      } else if (periodFilter === "este-ano") {
        matchesPeriod = eventDate.getFullYear() === today.getFullYear()
      } else if (periodFilter === "customizado" && startDate && endDate) {
        matchesPeriod = eventDate >= startDate && eventDate <= endDate
      }

      return matchesSearch && matchesType && matchesPeriod
    })
  }, [events, search, typeFilter, periodFilter, startDate, endDate])

  const now = new Date()

  const activeEvents = useMemo(
    () => filteredEvents.filter((e) => new Date(e.data_fim) >= now),
    [filteredEvents],
  )

  const pastEvents = useMemo(
    () => filteredEvents.filter((e) => new Date(e.data_fim) < now),
    [filteredEvents],
  )

  const handleViewMember = (id: string | number) => {
    setMemberId(id)
    setIsMemberModalOpened(true)
  }

  const handleCardClick = (evento: EventoDataType) => {
    navigate(`/events/${evento.id}`, { state: { readOnly: true } })
  }

  if (loading) {
    return (
      <div className="space-y-8 p-6 lg:p-10">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-[380px] w-full rounded-[2.5rem] bg-white/5" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="min-h-screen"
    >
      <PageHeader icon="bullseye-pointer" title="Minhas Inscrições" />

      <div className="mx-6 mb-8">
        <MySubscriptionsFilter
          search={search}
          onSearchChange={setSearch}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          periodFilter={periodFilter}
          onPeriodFilterChange={setPeriodFilter}
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
        />
      </div>

      <Tabs className="px-6" value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="bg-white/10">
          <TabsTrigger value="ativas" className="gap-2">
            Ativas
            <span className="flex size-5 items-center justify-center rounded-full bg-white/10 text-xs font-bold">
              {activeEvents.length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="passadas" className="gap-2">
            Passadas
            <span className="flex size-5 items-center justify-center rounded-full bg-white/10 text-xs font-bold">
              {pastEvents.length}
            </span>
          </TabsTrigger>
        </TabsList>

        {(["ativas", "passadas"] as TabType[]).map((tab) => {
          const tabData = tab === "ativas" ? activeEvents : pastEvents
          return (
            <TabsContent key={tab} value={tab}>
              <div className="relative min-h-[450px] pt-4">
                <AnimatePresence mode="wait">
                  {tabData.length > 0 ? (
                    <motion.div
                      key={tab}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      style={
                        { "--max-width": tabData.length <= 2 ? "500px" : "1fr" } as React.CSSProperties
                      }
                      className="grid grid-cols-[repeat(auto-fit,minmax(calc(5vw+300px),var(--max-width)))] gap-6"
                    >
                      {tabData.map((evento) => (
                        <motion.div
                          key={evento.id}
                          layout
                          transition={{ duration: 0.4, type: "spring", bounce: 0.2 }}
                        >
                          <EventCardV2
                            evento={{ ...evento, inscrito: true }}
                            onCardClick={() => handleCardClick(evento)}
                            onSubscribeEvent={() => {}}
                            onSubscribedCounterClick={(e) => {
                              e.stopPropagation()
                              handleCardClick(evento)
                            }}
                            onAvatarClcik={handleViewMember}
                          />
                        </motion.div>
                      ))}
                    </motion.div>
                  ) : (
                    <motion.div
                      key={`empty-${tab}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex flex-col items-center justify-center rounded-[3.5rem] border border-white/5 bg-white/[0.01] py-32 text-center"
                    >
                      <div className="relative mb-8">
                        <div className="absolute inset-0 animate-ping rounded-full bg-primary/10" />
                        <div className="relative flex size-24 items-center justify-center rounded-full border border-white/10 bg-zinc-900 shadow-2xl">
                          <i className="fi fi-ts-calendar-day text-4xl text-zinc-700" />
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-white">
                        {search
                          ? "Nenhum resultado encontrado"
                          : tab === "ativas"
                            ? "Nenhuma inscrição ativa"
                            : "Nenhum evento passado"}
                      </h3>
                      <p className="mt-2 max-w-xs text-zinc-500">
                        {search
                          ? "Tente ajustar seus filtros de busca."
                          : tab === "ativas"
                            ? "Você não tem eventos futuros inscritos."
                            : "Você ainda não participou de nenhum evento."}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </TabsContent>
          )
        })}
      </Tabs>

      <MemberModal
        isOpen={isMemberModalOpened}
        onClose={() => setIsMemberModalOpened(false)}
        memberId={memberId}
      />
    </motion.div>
  )
}

export default EventsMySubscriptionsPage
